import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { HSLColor } from '@yola/yola-palette-generator';
import { customization, dialogs, i18next } from '@yola/ws-sdk';
import isDeveloperMode from 'yola-editor/src/js/modules/user/accessors/is-developer-mode';
import CustomPaletteDialog from '../components/custom-palette-dialog';
import segment from '../../analytics/segment';
import helpers from '../helpers';
import dialogTypes from '../../dialogs/constants/dialog-types';
import {
  SUBMIT_BUTTON_TRIGGER_ID,
  DIALOG_BACKGROUND_TRIGGER_ID,
} from '../../dialogs/constants/common';
import dialogCallSourceIds from '../constants/dialog-call-source-ids';
import { CUSTOM_PALETTE_PREVIEW_COLOR_ORDER } from '../constants/color-schemes';
import triggerIds from '../constants/trigger-ids';
import advancedColorSectionIds from '../constants/advanced-color-section-ids';
import variableNames from '../constants/variable-names';
import { resolveVisualPreviewColor } from '../helpers/resolve-visual-preview-color';

const {
  trackers: { trackEvent },
  constants: { events },
} = segment;

const {
  updatePalettesWith,
  getAdjustedBaseColors,
  parseHslColorString,
  getDefaultColorSchemeLabels,
  compilePaletteIfNeeded,
  createPaletteEngine,
  updateLivePaletteElement,
} = helpers;

const getCaptions = () => ({
  title: i18next.t('Edit color palette'),
  description: i18next.t(
    'The color palette includes 6 customizable colors that control the overall appearance of your site'
  ),
  titleBreadcrumbs: [i18next.t('Design'), i18next.t('Colors')],
  customPaletteLabel: i18next.t('Custom palette'),
  colorSchemesLabel: i18next.t('Block color schemes'),
  resetTooltipLabel: i18next.t('Reset'),
  popoverInfo: i18next.t(
    'Your palette is used to generate 6 color schemes that can be applied to your blocks'
  ),
  buttons: {
    cancel: i18next.t('Cancel'),
    submit: i18next.t('Save'),
    editPalette: i18next.t('Edit palette'),
    addColorScheme: i18next.t('Add color scheme'),
  },
  resetDialog: {
    title: i18next.t('Confirm color reset'),
    description: i18next.t(
      "All your color adjustments will be reset to the default color palette. This action can't be undone."
    ),
    submit: i18next.t('Confirm'),
    cancel: i18next.t('Cancel'),
  },
});

function CustomPaletteContainer({
  getSharedData,
  onDialogMainAction,
  onDialogCancel,
  resolveSharedData,
}) {
  const dispatch = useDispatch();
  const {
    offsetX,
    offsetY,
    originPalette,
    engine: sharedEngine,
    carriedCustomPalettes,
    activePalette,
  } = getSharedData();

  const [dialogPosition, updatePosition] = useState({ offsetX, offsetY });
  const [engine] = useState(() => sharedEngine || createPaletteEngine(originPalette));
  const [paletteState, setPaletteState] = useState(() => engine.exportState());
  const [isDeveloperModeAvailable] = useState(() => isDeveloperMode());

  const parentPalette = useSelector((state) =>
    customization.selectors.getColorPalette(state, paletteState.parentId)
  );

  const previewColors = useMemo(
    () => CUSTOM_PALETTE_PREVIEW_COLOR_ORDER.map((id) => paletteState.colors[id]),
    [paletteState]
  );

  const colorSchemeOptions = useMemo(() => {
    const defaultSchemeEntries = getDefaultColorSchemeLabels().map(([schemeId, label]) => {
      const colorValue = paletteState.colors[schemeId];
      const color = new HSLColor(...Object.values(parseHslColorString(colorValue)));
      const overrides = paletteState.schemes?.[schemeId]?.variableOverrides || {};

      const parentBaseColor = new HSLColor(
        ...Object.values(parseHslColorString(parentPalette.colors[schemeId]))
      );
      const isBaseColorChanged = color.toColorString() !== parentBaseColor.toColorString();

      const { backgroundColor, isDarkTheme } = resolveVisualPreviewColor({
        color: overrides[variableNames.BLOCK_BACKGROUND] || colorValue,
        overrides,
      });

      return {
        label: i18next.t('{name} color scheme', { name: label }),
        value: schemeId,
        backgroundColor,
        isDarkTheme,
        isResetAvailable: isBaseColorChanged || Boolean(Object.keys(overrides).length),
      };
    });

    const customSchemeEntries = Object.entries(paletteState.schemes || {})
      .filter(([schemeId]) =>
        schemeId.startsWith(customization.constants.CUSTOM_COLOR_SCHEME_PREFIX)
      )
      .map(([schemeId, { baseColor, label, variableOverrides = {} }]) => {
        const { backgroundColor, isDarkTheme } = resolveVisualPreviewColor({
          color: variableOverrides[variableNames.BLOCK_BACKGROUND] || baseColor,
          overrides: variableOverrides,
        });

        return {
          label: i18next.t('{name} color scheme', { name: label }),
          value: schemeId,
          backgroundColor,
          isDarkTheme,
          isResetAvailable: Boolean(Object.keys(variableOverrides).length),
        };
      });

    return [...defaultSchemeEntries, ...customSchemeEntries];
  }, [paletteState, parentPalette]);

  const updateSharedData = (customData = {}) => {
    resolveSharedData({
      offsetX: dialogPosition.offsetX,
      offsetY: dialogPosition.offsetY,
      ...customData,
    });
  };

  const handleSubmit = ({ isClickOnOverlay } = {}) => {
    const submittedPalette = engine.exportState();

    updateLivePaletteElement(engine.getOutput());

    updateSharedData({
      carriedCustomPalettes: updatePalettesWith(submittedPalette, carriedCustomPalettes),
      carriedActivePaletteId: submittedPalette.id,
      engine: null,
    });

    onDialogMainAction({
      triggerId: isClickOnOverlay ? DIALOG_BACKGROUND_TRIGGER_ID : SUBMIT_BUTTON_TRIGGER_ID,
      colorsAdjusted: getAdjustedBaseColors(originPalette, submittedPalette),
    });

    dispatch(
      dialogs.actions.show(dialogTypes.DESIGN_SETTINGS_COLORS_DIALOG, {
        sourceId: dialogCallSourceIds.BACK_FROM_CUSTOM_PALETTE,
      })
    );
  };

  const handleCancel = () => {
    const compiledPalette = compilePaletteIfNeeded(activePalette);
    updateLivePaletteElement(compiledPalette);

    updateSharedData({ engine: null });
    onDialogCancel();
    dispatch(
      dialogs.actions.show(dialogTypes.DESIGN_SETTINGS_COLORS_DIALOG, {
        sourceId: dialogCallSourceIds.BACK_FROM_CUSTOM_PALETTE,
      })
    );
  };

  const onOverlayClick = () => {
    handleSubmit({ isClickOnOverlay: true });
  };

  const handleColorSelect = ({ colorSchemeId, isDevMode = false }) => {
    updateSharedData({ engine, isDeveloperMode: isDevMode });

    dispatch(
      dialogs.actions.show(dialogTypes.ADVANCED_COLORS_SETTINGS_DIALOG, {
        colorSchemeId,
        triggerId: triggerIds.COLOR_SCHEME,
      })
    );
  };

  const onDeveloperModeClick = ({ colorSchemeId }) =>
    handleColorSelect({ colorSchemeId, isDevMode: true });

  const handleColorSchemeReset = ({ colorSchemeId }) => {
    const compiledPalette = engine.resetColorScheme({
      colorSchemeId,
      initialColors: parentPalette.colors,
    });
    setPaletteState(engine.exportState());

    updateLivePaletteElement(compiledPalette);

    trackEvent(events.DESIGN_SETTINGS_RESET_TRIGGER_CLICKED, {
      dialogId: dialogTypes.DESIGN_SETTINGS_CUSTOM_PALETTE,
      triggerId: triggerIds.COLOR_SCHEME_SETTINGS,
      colorsAdjusted: [colorSchemeId],
      variableId: 'background-color',
      variableCategory: advancedColorSectionIds.GENERAL,
    });
  };

  const onEditPaletteClick = () => {
    updateSharedData({ engine });

    dispatch(
      dialogs.actions.show(dialogTypes.BASE_COLORS_PICKER_DIALOG, {
        triggerId: triggerIds.COLOR_PALETTE_SETTINGS,
        colorPreset: originPalette.id,
      })
    );
  };

  const handleAddColorScheme = () => {
    updateSharedData({ engine });

    trackEvent(events.ADD_COLOR_SCHEME_CLICKED, {
      triggerId: triggerIds.COLOR_PALETTE_SETTINGS,
    });

    dispatch(
      dialogs.actions.show(dialogTypes.ADD_COLOR_SCHEME_DIALOG, {
        triggerId: triggerIds.COLOR_PALETTE_SETTINGS,
      })
    );
  };

  const handleDragEnd = (_, { x, y }) => {
    updatePosition({
      offsetX: x,
      offsetY: y,
    });
  };

  const captions = getCaptions();

  return (
    <CustomPaletteDialog
      offsetX={dialogPosition.offsetX}
      offsetY={dialogPosition.offsetY}
      captions={captions}
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      colorSchemeOptions={colorSchemeOptions}
      previewColors={previewColors}
      onColorSelect={handleColorSelect}
      onDragEnd={handleDragEnd}
      onOverlayClick={onOverlayClick}
      onColorSchemeReset={handleColorSchemeReset}
      onEditPaletteClick={onEditPaletteClick}
      onAddColorScheme={handleAddColorScheme}
      {...(isDeveloperModeAvailable && { onDeveloperModeClick })}
    />
  );
}

CustomPaletteContainer.propTypes = {
  getSharedData: PropTypes.func.isRequired,
  resolveSharedData: PropTypes.func.isRequired,
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
};

export default CustomPaletteContainer;
